import React, { useMemo, useState } from "react";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Loader from "@amzn/meridian/loader";
import Expander from "@amzn/meridian/expander";
import AlertErrorWithMessage from "../alert/AlertErrorWithMessage";
import AuditDetailsTable from "./AuditDetailsTable";
import { formatSegment } from "./segmentTime";
import useOverallAuditData from "./useOverallAuditData";
import { ALERT_DEFAULT_MESSAGE, ALERT_AUTHENTICATION_MESSAGE, OVERALL_AUDIT_PAGE_SIZE } from "../../constants";
import { BusinessSpace, SegmentAuditSummary } from "../../admin-api/generated-src";

interface OverallAuditTabProps {
  nodeId: string | undefined;
  planGroupId: string | undefined;
  businessSpace: BusinessSpace | undefined;
  /** Flips when the audit page requests a refresh (5-min timer or manual button). */
  refreshToken: boolean;
  /** Facility UTC offset (minutes) for rendering segment times; null → UTC. */
  offsetMinutes?: number | null;
}

/**
 * Renders a percentage, guarding against divide-by-zero. Returns "0%" when the
 * denominator is zero or either value is missing.
 */
function toPercent(numerator?: number, denominator?: number): string {
  if (!denominator || denominator <= 0 || numerator == null) {
    return "0%";
  }
  return `${Math.round((numerator / denominator) * 100)}%`;
}

/**
 * Formats a segment's time range for the breakdown header. Prefers the seg-NN-NN
 * label; falls back to the segmentId when the pattern doesn't match.
 */
function segmentTitle(summary: SegmentAuditSummary, offsetMinutes: number | null): string {
  return formatSegment(
    {
      segmentId: summary.segmentId,
      segmentStart: summary.segmentStart,
      segmentEnd: summary.segmentEnd,
    },
    offsetMinutes,
  );
}

/**
 * The "Overall" tab of the audit page.
 *
 * Shows aggregate stats across all segments of a plan group, a collapsible per-segment
 * breakdown, and a merged scan table (with a Segment column) sourced from
 * GET /admin/audit/overall. Data is fetched + paginated by useOverallAuditData; this
 * component is agnostic to how the rows were assembled.
 *
 * Owns its own loading state — it is rendered OUTSIDE AuditPage's isAuditTableLoading
 * gate (that flag is owned by the per-segment fetch effect and can stick true across a
 * mid-load tab switch), so it can never be hidden behind a stale loader.
 */
export default function OverallAuditTab({
  nodeId,
  planGroupId,
  businessSpace,
  refreshToken,
  offsetMinutes = null,
}: OverallAuditTabProps) {
  const { stats, segmentBreakdown, records, loading, error, unauthorized, truncated } =
    useOverallAuditData(nodeId, planGroupId, businessSpace, refreshToken);

  // Segment epoch times keyed by segmentId, so the merged table's Segment column renders
  // the same labels as the tab bar and breakdown rather than re-parsing the raw id.
  const segmentTimeById = useMemo(
    () =>
      Object.fromEntries(
        segmentBreakdown.map((s) => [
          s.segmentId || "",
          { segmentStart: s.segmentStart, segmentEnd: s.segmentEnd },
        ])
      ),
    [segmentBreakdown]
  );

  // Per-segment expander open state, keyed by segmentId. Default collapsed.
  const [openSegments, setOpenSegments] = useState<Record<string, boolean>>({});

  const setSegmentOpen = (segmentId: string, open: boolean) => {
    setOpenSegments((prev) => ({ ...prev, [segmentId]: open }));
  };

  if (loading) {
    return <Loader />;
  }

  const totalAssociates = stats.totalAssociates ?? 0;
  const scannedCount = stats.scannedCount ?? 0;
  const notScannedCount = stats.notScannedCount ?? 0;

  return (
    <Column spacing="medium">
      {error && (
        <AlertErrorWithMessage
          message={unauthorized ? ALERT_AUTHENTICATION_MESSAGE : ALERT_DEFAULT_MESSAGE}
        />
      )}
      {truncated && (
        <AlertErrorWithMessage
          message={
            "This plan group returned more scans than can be shown at once. " +
            "Displaying a partial result — narrow by segment for complete data."
          }
        />
      )}

      {/* Aggregate stats (the fields the overall stats payload provides) */}
      <Row spacing="large" alignmentHorizontal="center" wrap="down">
        <Column spacing="none" alignmentHorizontal="center">
          <Text type="h300">{totalAssociates}</Text>
          <Text type="b200">Total Associates</Text>
        </Column>
        <Column spacing="none" alignmentHorizontal="center">
          <Text type="h300">{scannedCount}</Text>
          <Text type="b200">Total Scans</Text>
        </Column>
        <Column spacing="none" alignmentHorizontal="center">
          <Text type="h300">{toPercent(scannedCount, totalAssociates)}</Text>
          <Text type="b200">Scanned %</Text>
        </Column>
        <Column spacing="none" alignmentHorizontal="center">
          <Text type="h300">{notScannedCount}</Text>
          <Text type="b200">Not Scanned</Text>
        </Column>
      </Row>

      {/* Per-segment breakdown, collapsible (default collapsed) */}
      {segmentBreakdown.length > 0 && (
        <Column spacing="small">
          <Text type="h200">Per-Segment Breakdown</Text>
          {segmentBreakdown.map((summary) => {
            const segId = summary.segmentId || "";
            const segTotal = summary.totalAssociates ?? 0;
            const segScanned = summary.scannedCount ?? 0;
            return (
              <Expander
                key={segId}
                type="box"
                title={segmentTitle(summary, offsetMinutes)}
                open={!!openSegments[segId]}
                onChange={(open) => setSegmentOpen(segId, open)}
              >
                <Row spacing="large" wrap="down">
                  <Text>{`Scanned / Total: ${segScanned} / ${segTotal}`}</Text>
                  <Text>{`Scanned %: ${toPercent(segScanned, segTotal)}`}</Text>
                </Row>
              </Expander>
            );
          })}
        </Column>
      )}

      {/* Merged scan table with Segment column, 50/page, sortable across the full set */}
      <AuditDetailsTable
        auditRecordList={records}
        showSegmentColumn
        segmentTimeById={segmentTimeById}
        segmentOffsetMinutes={offsetMinutes}
        defaultItemsPerPage={OVERALL_AUDIT_PAGE_SIZE}
      />
    </Column>
  );
}
